<template>
  <div class="tabs-container d-flex flex-row fl-start fl-a-e">
    <div
      class="tab more-tabs"
      @click="toggleDropDownMenu"
      v-if="orderCount > maxOrderTabsToShow"
    >
      <div class="inner-tab">
        <p class="tab-name-wrapper">
          <span class="tab-name"
            >+{{ orderCount - maxOrderTabsToShow }}
            {{ $t("tabs.more-orders") }}</span
          >
        </p>
      </div>
      <SvgIcon
        :iconContent="currentChevron"
        iconStyle="width: 16px; height: 20px;"
      />
      <transition name="dropdown">
        <div v-if="isDropdownVisible" class="dropdown-menu">
          <ul>
            <li
              v-for="(_order, reference) in hiddenOrderTabs"
              :key="reference"
              @click="
                selectOrder(reference as string, { fromMoreOrders: true })
              "
            >
              {{ getTabName(reference as string) }}
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <div
      class="tab"
      v-for="(_order, reference, index) in visibleOrderTabs"
      :key="reference"
      :class="getTabClass(reference)"
      @click="selectOrder(reference)"
      :draggable="true"
      @dragstart="onDragStart(index)"
      @dragover.prevent
      @drop="onDrop(index)"
    >
      <div class="inner-tab" @click="showTabNameModal(reference)">
        <SvgIcon
          v-if="reference === selectedOrder?.reference"
          :iconContent="editPen"
          iconStyle="width: 20px; height: 20px;"
        />
        <p class="tab-name-wrapper">
          <span class="tab-name">{{ getTabName(reference) }}</span>
        </p>
      </div>
      <span
        class="close-button"
        v-if="reference === selectedOrder?.reference"
        @click.stop="closeTab()"
        data-cy="close-booking-tab-button"
      ></span>
    </div>
    <div
      v-if="hasSalesStarted"
      class="new-tab"
      @click="createOrder()"
      data-cy="add-booking-button"
    >
      <p>+</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { OrderJson } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";

import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { ModalStatus } from "@/constants/modal";
import { LogService } from "@/services/logging";
import CashFloatModule from "@/store/modules/cash-float";
import CustomersModule from "@/store/modules/customers";
import ModalModule from "@/store/modules/modal";
import FiskalyModule from "@/store/modules/fiskaly";
import { useOrderMetaStore } from "@/stores/order-meta.store";
// import { processAbortEvent } from "@/utils/helpers/compliance";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import chevronDown from "@/assets/images/svg/chevron-down.svg";
import chevronUp from "@/assets/images/svg/chevron-up.svg";

// modules
import {
  canCloseOrderTab,
  canCreateNewOrder,
  createNewOrder,
  maxOrderTabsToShow,
  removeOrder,
  selectOrder,
  swapOrders,
} from "@/utils/vue-helpers/orders.helper";

export default defineComponent({
  name: "NavbarTabs",
  async setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const { orders, selectedOrder, orderCount } = storeToRefs(orderStore);
    const orderMetaStore = useOrderMetaStore();

    const isDropdownVisible = ref(false);
    const draggedTabIndex = ref<number | null>(null);

    const hasSalesStarted = computed(() => {
      return CashFloatModule.cashFloat;
    });

    const currentChevron = computed(() => {
      return isDropdownVisible.value ? chevronUp : chevronDown;
    });

    const visibleOrderTabs = computed(() => {
      if (orderCount.value <= maxOrderTabsToShow) {
        return orders.value;
      }
      const orderKeys = Object.keys(orders.value);
      const visibleKeys = orderKeys.slice(
        orderCount.value - maxOrderTabsToShow
      );
      return visibleKeys.reduce((obj, key) => {
        obj[key] = orders.value[key];
        return obj;
      }, {} as Record<string, OrderJson>);
    });

    const hiddenOrderTabs = computed(() => {
      if (orderCount.value <= maxOrderTabsToShow) {
        return [];
      }
      const orderKeys = Object.keys(orders.value);
      const hiddenKeys = orderKeys.slice(
        0,
        orderCount.value - maxOrderTabsToShow
      );
      return hiddenKeys.reduce((obj, key) => {
        obj[key] = orders.value[key];
        return obj;
      }, {} as Record<string, OrderJson>);
    });

    function createOrder() {
      if (!canCreateNewOrder()) {
        return;
      }
      createNewOrder().catch(() => {
        // journaled + surfaced by the order helper; no tab is added
      });
    }

    async function closeTab() {
      if (!canCloseOrderTab()) {
        return;
      }
      if (!selectedOrder.value) {
        throw new Error(PSBErrors.NoBookingInModule);
      }
      LogService.info(
        `User clicked on Close Tab button to remove the order: ${selectedOrder.value?.reference}`
      );

      // remove customer of the booking
      const selectedCustomer = CustomersModule.selectedCustomers.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (c: any) => c.orderId === selectedOrder.value?.id
      );
      if (selectedCustomer) {
        CustomersModule.removeSelectedCustomer(selectedCustomer);
      }

      try {
        await removeOrder(selectedOrder.value as OrderJson);
      } catch {
        // journaled + surfaced by the order helper; the tab stays open
      }

      // // We need to remove the booking receipt signature from Fiskaly state
      // FiskalyModule.removeSignatureQrCodes(selectedOrder.value?.id);
      // // remove the transaction from Fiskaly state
      // FiskalyModule.removeTransaction(selectedOrder.value?.id);
    }

    function getTabClass(reference: string | null) {
      let className = "";

      if (
        orderCount.value === 1 ||
        reference === selectedOrder.value?.reference
      ) {
        className = "selected-tab";
      } else {
        className = "unselected-tab";
      }

      return className;
    }

    function showTabNameModal(reference: string | null) {
      if (reference === selectedOrder.value?.reference) {
        ModalModule.setStatus(ModalStatus.TabNameEdit);
      }
    }

    function getTabName(reference: string) {
      const order = reference ? orders.value[reference] : undefined;
      if (!order) {
        return `${t("tabs.order")} #${orderCount.value}`;
      }

      if (!order.tabName) {
        order.tabName = `${t("tabs.order")} #${orderMetaStore.nextTabNumber()}`;
      }
      return order.tabName;
    }

    function toggleDropDownMenu() {
      isDropdownVisible.value = !isDropdownVisible.value;
    }

    function onDragStart(index: number) {
      draggedTabIndex.value = index;
    }

    function onDrop(index: number) {
      if (draggedTabIndex.value !== null) {
        const visibleOrderKeys = Object.keys(visibleOrderTabs.value);
        const ref1 = visibleOrderKeys[index];
        const ref2 = visibleOrderKeys[draggedTabIndex.value];

        if (ref1 && ref2) {
          swapOrders({ ref1, ref2 });
        }

        draggedTabIndex.value = null;
      }
    }

    return {
      t,
      orders,
      visibleOrderTabs,
      hiddenOrderTabs,
      orderCount,
      selectedOrder,
      createOrder,
      selectOrder,
      closeTab,
      hasSalesStarted,
      getTabClass,
      editPen,
      currentChevron,
      showTabNameModal,
      getTabName,
      isDropdownVisible,
      toggleDropDownMenu,
      maxOrderTabsToShow,
      draggedTabIndex,
      onDragStart,
      onDrop,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

$height: 35px;
.tabs-container {
  .tab {
    margin-right: 5px;
    height: $height;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px 10px 0px 0px;
    cursor: pointer;

    p {
      line-height: 16px;
      height: 16px;
      letter-spacing: 0px;
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      text-align: center;
      position: relative;
      width: 100%;
    }

    .circle {
      display: inline-block;
      height: 25px;
      width: 25px;
      border-radius: 50%;
      position: absolute;
      top: -5px;
      left: 4px;
      font-weight: 400;
      font-style: normal;
      font-size: 13px;
      text-align: center;
      line-height: 25px;
      letter-spacing: 0px;
      color: white;
      background-color: $color-green-eagle;
    }
    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      font-weight: bold;
      font-style: normal;
      border: 1.5px solid black;
      border-radius: 50%;
      font-size: 12px;
      color: black;
      text-align: center;
      line-height: 1.5;
      cursor: pointer;
      margin-right: 5px;
    }

    .close-button::before {
      content: "x";
    }
  }

  .new-tab {
    background-color: transparent;
    margin-right: 5px;
    height: $height;
    width: 38px;
    line-height: $height;
    letter-spacing: 0px;
    color: rgb(255, 255, 255);
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: center;
    border-radius: 4px 4px 0px 0px;
    border: 1px solid white;
    border-bottom: none;
    cursor: pointer;
  }
  .selected-tab {
    width: 170px;
    background-color: #fafafa;
    color: $color-green-eagle;
  }
  .tab-with-animation {
    width: 170px;
    animation: colorSizeAnimation 1.5s ease-in-out forwards;
  }
  .unselected-tab {
    width: 120px;
    background-color: #0e2d34;
    color: #fafafa;
  }

  .inner-tab {
    display: flex;
    align-items: center;
    padding-left: 5px;
  }

  .selected-tab .tab-name-wrapper {
    max-width: 120px /* approx 70% of tab width */;
  }
  .unselected-tab .tab-name-wrapper {
    max-width: 110px /* adjusted to margin added */;
  }

  .tab-name-wrapper {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selected-tab .tab-name {
    padding-left: 30px;
    margin-left: -1.5rem;
    text-decoration: underline;
  }

  .unselected-tab .tab-name {
    padding-left: 20px;
  }

  .more-tabs {
    position: relative;
    width: 200px;
    background-color: #0e2d34;
    color: #fafafa;
    padding: 12px;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #fff;
    color: $color-green-eagle;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    width: 200px;
    z-index: 10;
    font-weight: 600;
  }
  .dropdown-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .dropdown-menu li {
    padding: 15px;
    cursor: pointer;
  }

  .dropdown-menu li:not(:last-child) {
    border-bottom: 1px solid $color-green-eagle; /* Add a separator line */
  }

  .dropdown-menu li:hover {
    background-color: #f0f0f0;
  }

  /* Animation styles */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  @keyframes colorSizeAnimation {
    0% {
      background-color: #0e2d34;
    }

    50% {
      background-color: orange;
    }

    100% {
      background-color: #fafafa;
    }
  }
}
</style>
